import type { UseTableProps } from '@pflegenavi/web-components';
import { useTable } from '@pflegenavi/web-components';
import type { GetTransactionsWithReceiptDto } from '@pflegenavi/shared/api';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

export enum TransactionStatusValues {
  ALL = 'All',
}

interface UseFmTransactionsTableResult
  extends UseTableProps<FmTransactionColumn, GetTransactionsWithReceiptDto> {
  reportingYear: Date;
  setReportingYear: (year: Date) => void;
  filterTerms: string;
  setFilterTerms: Dispatch<SetStateAction<string>>;
}

export enum FmTransactionColumn {
  SequenceID = 'sequenceID',
  Type = 'type',
  ReceiptType = 'receiptType',
  ServiceProvider = 'service provider',
  Amount = 'amount',
  Empty2 = 'empty2',
  Date = 'date',
  Receipt = 'receipt',
  Empty = 'empty',
}

export const useFmTransactionsTable = (): UseFmTransactionsTableResult => {
  const { setPage, setSelected, order, orderBy, ...tableProps } = useTable<
    GetTransactionsWithReceiptDto,
    FmTransactionColumn
  >({
    defaultRowsPerPage: 5,
    tableKey: 'familyMemberTransactions',
    defaultOrder: 'desc',
    defaultOrderBy: FmTransactionColumn.Date,
    sortKeys: {
      [FmTransactionColumn.SequenceID]: ['sequenceID'],
      [FmTransactionColumn.Type]: ['type'],
      [FmTransactionColumn.ServiceProvider]: ['serviceProvider'],
      [FmTransactionColumn.ReceiptType]: ['receiptType'],
      [FmTransactionColumn.Amount]: ['amount'],
      [FmTransactionColumn.Empty2]: [],
      [FmTransactionColumn.Date]: ['date', 'sequenceID', 'id'], // created_on is mapped to date in convertFamilyMemberTransactionPhoenixToNodeJS, so this is the transaction insertion timestamp, not just the date.
      [FmTransactionColumn.Receipt]: [],
      [FmTransactionColumn.Empty]: [],
    },
    sortComparator: {
      // descendingComparator expects a negative result when a comes first, so
      // this sorts the ids descending, like the backend does.
      id: (a, b) => {
        return b.localeCompare(a);
      },
    },
  });

  const [filterTerms, setFilterTerms] = useState('');
  const [reportingYear, setReportingYear] = useState(new Date());

  const handleSetReportingYear = (month: Date) => {
    setPage(0);
    setReportingYear(month);
  };

  return {
    filterTerms,
    setFilterTerms,
    reportingYear,
    setReportingYear: handleSetReportingYear,
    setPage,
    setSelected,
    order,
    orderBy,
    ...tableProps,
  };
};
